/**
 * Next.js basePath utilities for URL construction.
 *
 * Next.js auto-prepends basePath for <Link>, router.push(), and next/image,
 * but NOT for raw fetch(), EventSource, or WebSocket URLs.
 * Use these helpers for those cases.
 */

/** Prepend basePath to a path (for fetch, WebSocket, EventSource, etc.). */
export function withBasePath(path: string): string {
  const basePath = process.env['NEXT_PUBLIC_BASE_PATH'] || '';
  return `${basePath}${path}`;
}

/**
 * Strip a basePath prefix from a pathname, returning the App Router path.
 * Returns the pathname unchanged if basePath is empty or the pathname doesn't
 * start with it. Returns '/' if stripping leaves an empty string.
 *
 * When called with one argument, reads `NEXT_PUBLIC_BASE_PATH` from env.
 * When called with two arguments, uses the explicit basePath (for custom
 * servers or tests that need to avoid env coupling).
 */
export function stripBasePath(pathname: string, basePath?: string): string;
/** Overload accepting nullable pathname with an explicit basePath. */
export function stripBasePath(pathname: string | null, basePath: string): string | null;
/** Removes the basePath prefix from a pathname, falling back to the NEXT_PUBLIC_BASE_PATH env var. */
export function stripBasePath(pathname: string | null, basePath?: string): string | null {
  const bp = basePath ?? process.env['NEXT_PUBLIC_BASE_PATH'] ?? '';
  if (!bp) return pathname;
  if (pathname == null) return pathname;
  if (pathname === bp) return '/';
  if (pathname.startsWith(`${bp}/`)) {
    return pathname.slice(bp.length) || '/';
  }
  return pathname;
}

/**
 * Get the service-scoped path prefix for cookies and caches.
 * Returns '/<service>' when behind the dev-proxy, '/' otherwise.
 * Cookies set with this path are shared across worktrees of the same service.
 */
export function getServicePrefix(): string {
  const basePath = process.env['NEXT_PUBLIC_BASE_PATH'] || '';
  if (!basePath) return '/';
  // basePath is /<service>/<worktree>, extract /<service>
  const idx = basePath.indexOf('/', 1);
  return idx === -1 ? basePath : basePath.slice(0, idx);
}
